import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../../lib/cn';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** カードのバリアント */
  variant?: 'default' | 'elevated';
  /** パディングサイズ */
  padding?: 'none' | 'sm' | 'md' | 'lg';
  /** ホバーエフェクト */
  hoverable?: boolean;
}

export interface CardHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** タイトル */
  title?: ReactNode;
  /** 説明文 */
  description?: ReactNode;
  /** 右側のアクション */
  action?: ReactNode;
}

export type CardContentProps = HTMLAttributes<HTMLDivElement>;

export type CardFooterProps = HTMLAttributes<HTMLDivElement>;

/** @graph-connects none */
const variantStyles = {
  default: 'bg-[var(--color-bg-surface)] border border-[var(--color-border)]',
  elevated: 'bg-[var(--color-bg-elevated)] border border-[var(--color-border)] shadow-lg',
};

/** @graph-connects none */
const paddingStyles = {
  none: '',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
};

/**
 * @graph-stack web-design-system
 * @graph-domain UX
 * @graph-business カードコンテナ。default/elevatedバリアントとsm/md/lgパディング、ホバーエフェクトをサポートする角丸カードレイアウト
 * @graph-connects none
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    { variant = 'default', padding = 'md', hoverable = false, className, children, ...props },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          'rounded-xl',
          variantStyles[variant],
          paddingStyles[padding],
          hoverable &&
            'transition-all duration-[var(--transition-fast)] hover:border-[var(--color-border-hover)] hover:shadow-md',
          className,
        )}
        {...props}
      >
        {children}
      </div>
    );
  },
);

Card.displayName = 'Card';

/**
 * @graph-stack web-design-system
 * @graph-domain UX
 * @graph-business カードヘッダー。タイトル・説明文・右側アクションスロットを配置するカード上部セクション
 * @graph-connects none
 */
export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ title, description, action, className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('flex items-start justify-between gap-4', className)} {...props}>
        <div className="flex-1 min-w-0">
          {title && (
            <h3 className="text-lg font-semibold text-[var(--color-text-primary)] truncate">
              {title}
            </h3>
          )}
          {description && (
            <p className="mt-1 text-sm text-[var(--color-text-secondary)]">{description}</p>
          )}
          {children}
        </div>
        {action && <div className="flex-shrink-0">{action}</div>}
      </div>
    );
  },
);

CardHeader.displayName = 'CardHeader';

/**
 * @graph-stack web-design-system
 * @graph-domain UX
 * @graph-business カードコンテンツ。カード本文を配置するメインセクション
 * @graph-connects none
 */
export const CardContent = forwardRef<HTMLDivElement, CardContentProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('mt-4', className)} {...props}>
        {children}
      </div>
    );
  },
);

CardContent.displayName = 'CardContent';

/**
 * @graph-stack web-design-system
 * @graph-domain UX
 * @graph-business カードフッター。ボーダー付きのカード下部アクションセクション
 * @graph-connects none
 */
export const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('mt-6 pt-4 border-t border-[var(--color-border)]', className)}
        {...props}
      >
        {children}
      </div>
    );
  },
);

CardFooter.displayName = 'CardFooter';
